import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  List
} from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import * as MuiIcons from '@mui/icons-material';
import PropTypes from 'prop-types';

/**
 * MenuItem - Recursive menu item component
 * 
 * Phase 5: M5.T5.2 - Dynamic Sidebar
 * 
 * Features:
 * - Recursive rendering for nested menus
 * - Dynamic Material-UI icon rendering
 * - Active route highlighting
 * - Collapsible sub-menus
 * - Indentation for nested items
 * 
 * Usage:
 * <MenuItem item={menuItem} currentPath={location.pathname} />
 */
const MenuItem = ({ item, currentPath, depth = 0 }) => {
  const [open, setOpen] = useState(false);
  
  // Sort children by order (if exists)
  const sortedChildren = item.children && item.children.length > 0
    ? [...item.children].sort((a, b) => (a.order || 0) - (b.order || 0))
    : [];
  
  const hasChildren = sortedChildren.length > 0;
  const isActive = currentPath === item.path;
  
  // Get icon component dynamically from Material-UI
  // Example: item.icon = "DashboardIcon" → DashboardIcon component
  const IconComponent = item.icon ? MuiIcons[item.icon] : null;
  
  // Parent menu item (has children) - No link, just expand/collapse
  // This creates an accordion/submenu structure
  if (hasChildren) {
    return (
      <>
        <ListItemButton 
          onClick={() => setOpen(!open)}
          sx={{ 
            pl: 2 + depth * 2, // Indent based on depth
            '&:hover': {
              backgroundColor: 'action.hover'
            }
          }}
        >
          {IconComponent && (
            <ListItemIcon sx={{ minWidth: 40 }}>
              <IconComponent fontSize="small" />
            </ListItemIcon>
          )}
          <ListItemText 
            primary={item.name}
            primaryTypographyProps={{
              fontSize: '0.875rem',
              fontWeight: 500
            }}
          />
          {open ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
        
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {sortedChildren.map(child => (
              <MenuItem
                key={child.code}
                item={child}
                currentPath={currentPath}
                depth={depth + 1}
              />
            ))}
          </List>
        </Collapse>
      </>
    );
  }
  
  // Leaf menu item (no children) - Has link
  // If path is empty, default to /dashboard
  const targetPath = item.path || '/dashboard';
  
  return (
    <ListItemButton
      component={Link}
      to={targetPath}
      selected={isActive}
      sx={{ 
        pl: 2 + depth * 2, // Indent based on depth
        '&.Mui-selected': {
          backgroundColor: 'primary.light',
          '&:hover': {
            backgroundColor: 'primary.light'
          }
        }
      }}
    >
      {IconComponent && (
        <ListItemIcon sx={{ minWidth: 40 }}>
          <IconComponent fontSize="small" />
        </ListItemIcon>
      )}
      <ListItemText 
        primary={item.name}
        primaryTypographyProps={{
          fontSize: '0.875rem',
          fontWeight: isActive ? 600 : 400
        }}
      />
    </ListItemButton>
  );
};

MenuItem.propTypes = {
  item: PropTypes.shape({
    code: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    path: PropTypes.string,
    icon: PropTypes.string,
    children: PropTypes.array,
    parentId: PropTypes.string
  }).isRequired,
  currentPath: PropTypes.string.isRequired,
  depth: PropTypes.number
};

export default MenuItem;

